import { ImageWithFallback } from '@/components/ImageWithFallback';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import type { Product } from '@/types/products';
import { Loader2, Search, Sparkles, Wand2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface ProductListProps {
  products: Product[];
  onProductClick: (productId: string | number) => void;
  selectedProducts: Product[];
  isGeneratingAR: boolean;
  generatingARProductId: string | number | null;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoading: boolean;
  onPromptSubmit?: (query: string) => void;
}

export default function ProductList({
  products,
  onProductClick,
  selectedProducts,
  isGeneratingAR,
  generatingARProductId,
  hasMore,
  onLoadMore,
  isLoading,
  onPromptSubmit,
}: ProductListProps) {
  console.log(hasMore, onLoadMore);
  const [promptText, setPromptText] = useState('');

  const observer = useRef<IntersectionObserver | null>(null);
  const lastProductRef = useRef<HTMLDivElement>(null);

  const visibleProducts = (products || []).slice(0, 10);

  const handlePromptSubmit = () => {
    if (!promptText.trim()) return;

    onPromptSubmit?.(promptText.trim());
    setPromptText('');
  };

  // Temporarily disable infinite scroll: we only show first 10 products
  useEffect(() => {
    return () => {
      observer.current?.disconnect();
    };
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Sticky controls: prompt textarea + AI Magic + heading */}
      <div className=" pb-3 bg-white sticky -top-4 z-10">
        <div className="space-y-2 mb-3">
          <Textarea
            placeholder="e.g., I need a modern sofa in blue color for a living room..."
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            className="min-h-[100px] max-h-[150px] resize-none overflow-y-auto border-gray-200 bg-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handlePromptSubmit();
              }
            }}
          />
          <Button
            className="w-full bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!promptText.trim()}
            onClick={handlePromptSubmit}
          >
            <Wand2 className="h-4 w-4 mr-2" />
            AI Magic
          </Button>
        </div>

        <h3 className="text-gray-900 font-medium mt-1">
          Recommended Products ({visibleProducts.length})
        </h3>
      </div>

      {/* Products List */}
      <div className="flex-1 flex flex-col mt-3">
        <div className="flex-1 overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-3">
            {visibleProducts.map((product, index) => {
              const isSelected = selectedProducts.some(
                (p) => p.id === product.id
              );
              const isLastElement = index === visibleProducts.length - 1;

              return (
                <div
                  key={`${product.id}-${index}`}
                  ref={isLastElement ? lastProductRef : null}
                >
                  <Card
                    className={`group transition-all relative ${
                      isGeneratingAR && generatingARProductId !== product.id
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                    onClick={() =>
                      !isGeneratingAR && onProductClick(product?.id || '')
                    }
                  >
                    <CardContent className="p-3">
                      <div className="aspect-square rounded-lg overflow-hidden mb-2 bg-gray-100 relative">
                        <ImageWithFallback
                          isStorageUrl={
                            !product.images?.[0]?.originalUrl?.startsWith(
                              'http'
                            )
                          }
                          src={
                            product.images?.[0]?.originalUrl ||
                            product.image_urls?.[0] ||
                            ''
                          }
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        />
                        {generatingARProductId === product.id && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                            <div className="text-center text-white">
                              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-1" />
                              <p className="text-xs">Preparing AR view...</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-1">
                          <h4 className="text-xs text-gray-900 line-clamp-2 leading-tight">
                            {product.name}
                          </h4>
                          {isSelected && (
                            <Sparkles className="h-3 w-3 text-black flex-shrink-0" />
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-900 font-medium">
                            $
                            {(
                              product?.stocks?.[0] as unknown as {
                                price: string;
                              }
                            )?.price || 'N/A'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex gap-1">
                            {product.color && (
                              <div
                                className="w-2.5 h-2.5 rounded-full border border-gray-200"
                                style={{ backgroundColor: product.color }}
                              />
                            )}
                          </div>
                          <Badge
                            variant={
                              product.stocks?.length
                                ? 'secondary'
                                : 'destructive'
                            }
                            className={`text-xs px-1 py-0 ${
                              product.stocks?.length
                                ? 'bg-gray-100 text-gray-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {product.stocks?.length
                              ? 'In Stock'
                              : 'Out of Stock'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>

          {visibleProducts.length === 0 && !isLoading && (
            <div className="text-center py-8">
              <Search className="h-8 w-8 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500 text-sm">No products found</p>
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
